import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMap } from "react-map-gl"; // Import Mapbox components
import { delete_polygon, search_map, stree_view, measure_dims } from "../../../assets/images";
import { inputsActions, usePrevious } from "../../Redux";
import { Popconfirm, Tooltip } from "antd";
import { ToolbarWrap } from "./styles";
import { DrawPolygon, SearchLocations, PolygonTools, MapboxExportMenu, MeasureTool, MeasureToolBar, DrawMultiPolygon } from "./";
import { MapboxLayerMenu } from "./MapboxLayerMenu";
import { useMapboxDraw, MeasureMode } from "../DrawToolProvider";

const MapboxToolbar = () => {
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();
  const dispatch = useDispatch();
  const draw = useMapboxDraw();

  // Store
  const selectedFeatureId = useSelector((state) => state.inputs.selectedFeatureId);
  const searchVisible = useSelector((state) => state.inputs.uiState.search_visible);
  const isDrawing = useSelector((state) => state.inputs.uiState.is_drawing);
  const is_measuring = useSelector((state) => state.inputs.uiState.is_measuring);
  const isDrawingPolyline = useSelector((state) => state.inputs.uiState.is_drawing_polyline);
  const is_drawing_multi = useSelector((state) => state.inputs.uiState.is_drawing_multi);
  const generated = useSelector((state) => state.sift.ioManager.outputs.runState.generated);
  const prevSelectedId = usePrevious(selectedFeatureId);
  const additional_layers = useSelector((state) => state.inputs.uiState.additional_layers);

  const new_project_loading = useSelector((state) => state.sift.projectManager.new_project_loading);
  const prev_new_project_loading = usePrevious(new_project_loading);

  const show_azi = useSelector((state) => state.inputs.uiState.show_azi);
  const show_dims = useSelector((state) => state.inputs.uiState.show_dims);

  // Use ref to keep track of additional_layers state
  const additional_layers_ref = useRef(additional_layers);
  const is_drawing_multi_ref = useRef(is_drawing_multi);
  const is_measuring_ref = useRef(is_measuring);
  const isDrawing_ref = useRef(isDrawing);

  useEffect(() => {
    additional_layers_ref.current = additional_layers;
  }, [additional_layers]);

  useEffect(() => {
    is_drawing_multi_ref.current = is_drawing_multi;
  }, [is_drawing_multi]);

  useEffect(() => {
    is_measuring_ref.current = is_measuring;
  }, [is_measuring]);

  useEffect(() => {
    isDrawing_ref.current = isDrawing;
  }, [isDrawing]);

  // State
  const [expandMapLayers, setExpandMapLayers] = useState(false);
  const [expandPolygonTools, setExpandPolygonTools] = useState(false);
  const [expandExportTools, setExpandExportTools] = useState(false);
  const [expandHeight, setExpandHeight] = useState(40); // Initialize with a default height

  // Refs
  const expandMapLayersRef = useRef(expandMapLayers);
  const expandExportToolsRef = useRef(expandExportTools);

  useEffect(() => {
    expandMapLayersRef.current = expandMapLayers;
  }, [expandMapLayers]);

  useEffect(() => {
    expandExportToolsRef.current = expandExportTools;
  }, [expandExportTools]);

  // Close expanded menus when the map is clicked
  useEffect(() => {
    if (map) {
      const handleClick = () => {
        // Don't close menus when in multi-select mode
        if (is_drawing_multi) return;

        setExpandMapLayers(false);
        setExpandExportTools(false);
        if (searchVisible) {
          handleToggleSearch();
        }
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }
  }, [map, searchVisible, is_drawing_multi]);

  useEffect(() => {
    const hasSelection = selectedFeatureId && (Array.isArray(selectedFeatureId) ? selectedFeatureId.length > 0 : true);
    if (hasSelection) {
      handleTogglePolygonTools(true);
    } else {
      // Don't close the polygon tools if we're in multi-select mode
      if (!is_drawing_multi) {
        handleTogglePolygonTools(false);
      }
    }
  }, [selectedFeatureId, is_drawing_multi]);

  useEffect(() => {
    if (!map) return;
    // this clears measurements on the map if they are present and a user clicked "new project"
    if (prev_new_project_loading == true && new_project_loading == false) {
      cancel_measuring();
    }
  }, [new_project_loading]);

  const cancel_measuring = () => {
    if (!draw || !map) return;

    if (map.getLayer("measure-text-layer")) {
      map.removeLayer("measure-text-layer");
    }

    if (map.getSource("measure-text-layer")) {
      map.removeSource("measure-text-layer");
    }

    const allFeatures = draw.getAll().features;
    const measureLines = allFeatures.filter((feature) => feature.properties.isMeasureLine);

    measureLines.forEach((line) => {
      draw.delete(line.id);
    });

    draw.changeMode("simple_select");
    dispatch(inputsActions.update_ui_state("is_measuring", false));
    map.getCanvas().style.cursor = "";
    MeasureMode.cleanup(map);
  };

  // Centralized tool state manager - switches between different tool modes cleanly.
  const switchToolMode = useCallback(
    (newMode) => {
      console.log("Switching to tool mode:", newMode);

      // Clean up tool states that we're NOT switching to
      if (newMode !== "draw" && isDrawing) {
        draw.changeMode("simple_select");
        dispatch(inputsActions.update_ui_state("is_drawing", false));
      }
      if (newMode !== "draw" && isDrawingPolyline) {
        dispatch(inputsActions.update_ui_state("is_drawing_polyline", false));
      }
      if (newMode !== "multi_select" && is_drawing_multi_ref.current) {
        dispatch(inputsActions.update_ui_state("is_drawing_multi", false));
      }
      if (newMode !== "measure" && is_measuring) {
        cancel_measuring();
      }

      // Activate the new mode with this switch
      switch (newMode) {
        case "draw":
          // Clear search and menus for tool modes
          if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
          setExpandMapLayers(false);
          setExpandExportTools(false);
          setExpandPolygonTools(false);
          dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
          draw.changeMode("draw_polygon");
          dispatch(inputsActions.update_ui_state("is_drawing", true));
          break;

        case "multi_select":
          // Clear search and menus for tool modes
          if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
          setExpandMapLayers(false);
          setExpandExportTools(false);
          setExpandPolygonTools(false);
          dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
          draw.changeMode("simple_select");
          dispatch(inputsActions.update_ui_state("is_drawing_multi", true));
          map.getCanvas().style.cursor = "crosshair";
          break;

        case "measure":
          // Clear search and menus for tool modes
          if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
          setExpandMapLayers(false);
          setExpandExportTools(false);
          setExpandPolygonTools(false);
          dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
          draw.changeMode("measure_mode");
          dispatch(inputsActions.update_ui_state("is_measuring", true));
          break;

        case "select_feature":
          // Keep feature selected, just clear other tools
          const hasSelection = selectedFeatureId && (Array.isArray(selectedFeatureId) ? selectedFeatureId.length > 0 : true);
          if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
          setExpandMapLayers(false);
          setExpandExportTools(false);
          setExpandPolygonTools(hasSelection);
          break;

        case "none":
        default:
          if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
          setExpandMapLayers(false);
          setExpandExportTools(false);
          setExpandPolygonTools(false);
          dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
          draw.changeMode("simple_select");
          map.getCanvas().style.cursor = "";
          break;
      }

      setExpandHeight(40);
    },
    [isDrawing, isDrawingPolyline, is_measuring, searchVisible, draw, map, dispatch],
  );

  // Listen for tool switch events from child components.
  useEffect(() => {
    if (!map) return;

    const handleDrawPolygon = () => {
      if (isDrawing_ref.current) {
        switchToolMode("none");
      } else {
        switchToolMode("draw");
      }
    };

    const handleToggleMultiSelect = () => {
      if (is_drawing_multi_ref.current) {
        switchToolMode("none");
      } else {
        switchToolMode("multi_select");
      }
    };

    const handleMeasure = () => {
      console.log("handleMeasure called, is_measuring_ref:", is_measuring_ref.current);
      if (is_measuring_ref.current) {
        console.log("Turning off measure mode");
        switchToolMode("none");
      } else {
        console.log("Turning on measure mode");
        switchToolMode("measure");
      }
    };

    map.on("draw.polygon", handleDrawPolygon);
    map.on("toggle.multiselect", handleToggleMultiSelect);
    map.on("measure_mode", handleMeasure);

    return () => {
      map.off("draw.polygon", handleDrawPolygon);
      map.off("toggle.multiselect", handleToggleMultiSelect);
      map.off("measure_mode", handleMeasure);
    };
  }, [map, switchToolMode]);

  // Handle toggling functions
  const toggleExpandState = useCallback((setExpandFn, stateValue) => {
    const newHeight = stateValue ? 40 : 435; // Set height based on which tool is expanded (standard toolbar height vs layer menu height)
    setExpandFn(!stateValue);
    setExpandHeight(newHeight); // make sure the expandHeight is updated correctly here
  }, []);

  const handleToggleSearch = useCallback(() => {
    if (!searchVisible) {
      // Clear all the other tools when opening search
      if (isDrawing) {
        draw.changeMode("simple_select");
        dispatch(inputsActions.update_ui_state("is_drawing", false));
      }
      if (isDrawingPolyline) dispatch(inputsActions.update_ui_state("is_drawing_polyline", false));
      if (is_drawing_multi_ref.current) dispatch(inputsActions.update_ui_state("is_drawing_multi", false));
      if (is_measuring) cancel_measuring();
      setExpandMapLayers(false);
      setExpandExportTools(false);
      setExpandPolygonTools(false);
    }
    dispatch(inputsActions.update_ui_state("search_visible", !searchVisible));
  }, [searchVisible, isDrawing, isDrawingPolyline, is_measuring, draw, dispatch]);

  const handleToggleMapLayers = useCallback(() => {
    // Clear all the other tools when opening map layers
    if (!expandMapLayers) {
      if (isDrawing) {
        draw.changeMode("simple_select");
        dispatch(inputsActions.update_ui_state("is_drawing", false));
      }
      if (isDrawingPolyline) dispatch(inputsActions.update_ui_state("is_drawing_polyline", false));
      if (is_drawing_multi_ref.current) dispatch(inputsActions.update_ui_state("is_drawing_multi", false));
      if (is_measuring) cancel_measuring();
      if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
      setExpandExportTools(false);
      setExpandPolygonTools(false);
    }
    toggleExpandState(setExpandMapLayers, expandMapLayers);
  }, [expandMapLayers, isDrawing, isDrawingPolyline, is_measuring, searchVisible, draw, dispatch, toggleExpandState]);

  const handleTogglePolygonTools = useCallback((toggled) => {
    setExpandPolygonTools(toggled);
    setExpandHeight(40);
  }, []);

  const handleToggleExportTools = useCallback(() => {
    // Clear all the other tools when opening export tools
    if (!expandExportTools) {
      if (isDrawing) {
        draw.changeMode("simple_select");
        dispatch(inputsActions.update_ui_state("is_drawing", false));
      }
      if (isDrawingPolyline) dispatch(inputsActions.update_ui_state("is_drawing_polyline", false));
      if (is_drawing_multi_ref.current) dispatch(inputsActions.update_ui_state("is_drawing_multi", false));
      if (is_measuring) cancel_measuring();
      if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
      setExpandMapLayers(false);
      setExpandPolygonTools(false);
    }
    toggleExpandState(setExpandExportTools, expandExportTools);
  }, [expandExportTools, isDrawing, isDrawingPolyline, is_measuring, searchVisible, draw, dispatch, toggleExpandState]);

  const resetExpandedStates = useCallback(() => {
    switchToolMode("none");
  }, [switchToolMode]);

  // Update Mapbox based on the selected feature
  useEffect(() => {
    const hasSelection = selectedFeatureId && (Array.isArray(selectedFeatureId) ? selectedFeatureId.length > 0 : true);
    if (hasSelection) {
      handleTogglePolygonTools(true);
      if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
      if (is_measuring) cancel_measuring();
    } else {
      // Don't close polygon tools or cancel measuring if we're switching to measure mode or multi-select
      if (!is_measuring && !is_drawing_multi) {
        handleTogglePolygonTools(false);
      }
    }
    if (prevSelectedId !== selectedFeatureId) setExpandHeight(40);
  }, [selectedFeatureId, prevSelectedId]);

  useEffect(() => {
    if (isDrawingPolyline) {
      resetExpandedStates();
    }
  }, [isDrawingPolyline]);

  useEffect(() => {
    if (is_drawing_multi) {
      // When sstarting multi-select mode, just close search and clear the selection
      // Don't call resetExpandedStates as it would turn off is_drawing_multi thingy and cause the tool to not work
      if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
      if (selectedFeatureId) dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
      setExpandMapLayers(false);
      setExpandPolygonTools(false);
      setExpandExportTools(false);
    }
  }, [is_drawing_multi]);

  const deleteAll = () => {
    map.fire("deleteAllFeatures");
    cancel_measuring();
  };

  const handleToggleDims = () => {
    let checked = !show_dims;
    dispatch(inputsActions.update_ui_state("show_dims", checked));
    // if (show_azi) dispatch(inputsActions.update_ui_state("show_azi", false));
  };

  const handleToggleAzi = () => {
    let checked = !show_azi;
    dispatch(inputsActions.update_ui_state("show_azi", checked));
    // if (show_dims) dispatch(inputsActions.update_ui_state("show_dims", false));
  };

  return (
    <ToolbarWrap expandHeight={expandHeight} expand={searchVisible || expandMapLayers}>
      <section className="map-action-buttons">
        <Tooltip placement="left" title="Search" mouseEnterDelay={0.5}>
          <button className={searchVisible ? "button-active" : ""} onClick={handleToggleSearch}>
            <img draggable={false} src={search_map} />
          </button>
        </Tooltip>
        <Tooltip placement="bottom" title="Change Basemap" mouseEnterDelay={0.5}>
          <button className={expandMapLayers ? "button-active" : ""} onClick={handleToggleMapLayers}>
            <img draggable={false} src={stree_view} />
          </button>
        </Tooltip>
        <MeasureTool />
        <DrawMultiPolygon />
        <DrawPolygon />
        <Tooltip placement="bottom" title="Show Polygon Dimensions" mouseEnterDelay={0.25}>
          <button className={show_dims ? "button-active" : ""} style={{ padding: 0 }} disabled={false} onClick={() => handleToggleDims()}>
            <img draggable={false} src={measure_dims} />
          </button>
        </Tooltip>
        <Tooltip placement="bottom" title="Show Polygon Azimuth" mouseEnterDelay={0.25}>
          <button className={show_azi ? "button-active" : ""} style={{ padding: 0 }} disabled={false} onClick={() => handleToggleAzi()}>
            {/* <img draggable={false} src={protractor} /> */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_427_191)">
                <path
                  d="M19.8 3.93697L16.8908 1.03168C16.6742 0.815156 16.4971 0.638004 16.1074 1.03168L14.887 2.25206C11.1865 -0.976048 5.88371 -0.629617 2.62018 2.63392C-0.741777 5.99193 -0.867752 11.3774 2.23831 14.8889L1.01793 16.1093C0.624263 16.503 0.915579 16.7903 1.01793 16.8927L3.92717 19.798C4.03739 19.9043 4.32084 20.1917 4.71057 19.798L19.8 4.72038C20.1937 4.3267 19.9023 4.03932 19.8 3.93697ZM17.7293 5.22034L16.9734 4.46449C16.7569 4.24797 16.4065 4.24797 16.19 4.46449C15.9735 4.68101 15.9735 5.03138 16.19 5.2479L16.9459 6.00374L16.0522 6.89738L15.6901 6.53914C15.4735 6.32262 15.1232 6.32262 14.9067 6.53914C14.6901 6.75566 14.6901 7.10602 14.9067 7.32254L15.2649 7.68078L14.3713 8.57048L13.6194 7.81463C13.4028 7.59811 13.0525 7.59811 12.8359 7.81463C12.6194 8.03115 12.6194 8.38152 12.8359 8.59804L13.5918 9.35389L12.6982 10.2436L12.3399 9.88534C12.1234 9.66882 11.773 9.66882 11.5565 9.88534C11.34 10.1019 11.34 10.4522 11.5565 10.6687L11.9148 11.027L11.0211 11.9206L10.2653 11.1648C10.0488 10.9483 9.69839 10.9483 9.48187 11.1648C9.26535 11.3813 9.26535 11.7317 9.48187 11.9482L10.2377 12.704L9.34408 13.5937L8.9819 13.2355C8.76539 13.019 8.41502 13.019 8.1985 13.2355C7.98198 13.452 7.98198 13.8024 8.1985 14.0189L8.56068 14.3771L7.66704 15.2668L6.91119 14.511C6.69467 14.2945 6.34431 14.2945 6.12779 14.511C5.91127 14.7275 5.91127 15.0779 6.12779 15.2944L6.88364 16.0502L5.99 16.9439L5.63176 16.5856C5.41524 16.3691 5.06488 16.3691 4.84836 16.5856C4.63184 16.8021 4.63184 17.1525 4.84836 17.369L5.2066 17.7273L4.3169 18.6209L2.19501 16.499L3.40358 15.2905C3.80513 14.8889 3.50594 14.6094 3.40358 14.507C0.344756 11.4482 0.344756 6.47221 3.40358 3.41339C6.46635 0.354561 11.4463 0.358498 14.5051 3.41339C14.7216 3.62991 14.9185 3.78344 15.2885 3.41339L16.4971 2.20482L18.619 4.3267L17.7293 5.22034Z"
                  fill="#333333"
                />
                <path
                  d="M12.5092 5.40532C10.5527 3.44483 7.20254 3.59837 5.39165 5.40532C3.42723 7.3658 3.42723 10.5585 5.39165 12.519C5.494 12.6213 5.78532 12.9126 6.17506 12.519L12.5092 6.18872C12.9029 5.79505 12.6116 5.50767 12.5092 5.40532ZM5.81288 11.3143C4.65942 9.77901 4.78146 7.58232 6.17899 6.18872C7.64739 4.72033 9.86769 4.70852 11.3085 5.82261L5.81288 11.3143Z"
                  fill="#333333"
                />
              </g>
              <defs>
                <clipPath id="clip0_427_191">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </Tooltip>

        <Tooltip placement="bottom" title="Delete All" mouseEnterDelay={0.5}>
          <Popconfirm title="Are you sure?" onConfirm={deleteAll}>
            <button disabled={generated}>
              <img draggable={false} src={delete_polygon} />
            </button>
          </Popconfirm>
        </Tooltip>
        <MapboxExportMenu />
      </section>

      <section className={searchVisible || expandMapLayers || expandPolygonTools || expandExportTools || is_measuring ? "expand-down" : "map-action-tray"}>
        {searchVisible && <SearchLocations />}
        {expandMapLayers && <MapboxLayerMenu additional_layers={additional_layers_ref.current} />}
        {is_measuring && <MeasureToolBar />}
        {selectedFeatureId && expandPolygonTools && !searchVisible && <PolygonTools id={selectedFeatureId} />}
      </section>
    </ToolbarWrap>
  );
};

export { MapboxToolbar };
